import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { ProjectFilters } from '@/data/services/projectServices';
import { projectsQuerykeys } from '@/data/services/querykeys';
import { useEffect, useState } from 'react';
import ProjectCarousel, { ProjectsSections } from './ProjectCarousel';
import useManualQuery from '@/data/hook/query/v1/useManualQuery';
import Observable from '@/utils/observers/ObserverPattern';
import { Project } from '@/models/Project';

type ProjectCarouselProps = {
  section: ProjectsSections;
  filters?: ProjectFilters;
  route?: string;
  invalidatesObserver?: Observable<number, Project>;
  hideSeeAll?: boolean;
  transformProjects?: (projects: Project[]) => Project[];
};

export default function ProjectCarouselFetcher({
  filters,
  invalidatesObserver,
  transformProjects,
  ...projectCarouselProps
}: ProjectCarouselProps) {
  const [carrouselFilters, setCarrouselFilters] = useState({});
  const allFilters = { ...filters, ...carrouselFilters };

  const { queryFn, queryKey } =
    projectsQuerykeys.list(allFilters)._ctx.infinity;

  const {
    results: projects,
    isInitialLoading,
    isFetchingNextPage: fetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteService({
    queryFn,
    queryKey,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { update } = useManualQuery({ queryKey, queryFn });

  const onFilter = (value: ProjectFilters) => {
    setCarrouselFilters(prev => ({ ...prev, ...value }));
  };

  useEffect(
    () =>
      invalidatesObserver?.subscribe({
        id: NaN,
        action: projet => update(projet as Partial<Project>),
      }),
    [update, invalidatesObserver],
  );

  return (
    <ProjectCarousel
      {...projectCarouselProps}
      error={error}
      filters={allFilters}
      onFilter={onFilter}
      projects={transformProjects ? transformProjects(projects) : projects}
      onMutate={project => invalidatesObserver?.notifyAll(1, project)}
      fetchingNextPage={fetchingNextPage}
      hasNextPage={hasNextPage}
      isLoading={isInitialLoading}
      onReachEnd={fetchNextPage}
    />
  );
}
